import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useHotel } from "@/hooks/useHotel";
import { useCategories } from "@/hooks/useCategories.hooks";
import { CategoryEntityType } from "@/types/category";
import { Input } from "@/components/ui/input";
import { Search, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useCannedResponses } from "@/components/pages/canned-responses/hooks/canned-responses.hooks";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface CannedResponseSelectorProps {
  onSelect: (text: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showTrigger?: boolean;
}

export const CannedResponseSelector: React.FC<CannedResponseSelectorProps> = ({
  onSelect,
  open,
  onOpenChange,
  showTrigger = true,
}) => {
  const { t } = useTranslation();
  const { hotelId } = useHotel();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const [isOpenState, setIsOpenState] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<string | null>(null);

  const isOpen = open !== undefined ? open : isOpenState;
  const setIsOpen = onOpenChange !== undefined ? onOpenChange : setIsOpenState;

  const { data: categoriesData } = useCategories({
    hotelId: hotelId || "",
    entityType: CategoryEntityType.CANNED_RESPONSE,
    page: 1,
    limit: 100,
  });

  const { data: responsesData, isLoading } = useCannedResponses({
    hotelId: hotelId || "",
    categoryId: selectedCategoryId || categoriesData?.items[0]?.id || "",
  });

  const categories = categoriesData?.items || [];
  const responses = responsesData?.items || [];

  const filteredResponses = responses.filter((response) =>
    response.text.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelect = (text: string) => {
    setSelectedResponse(text);
  };

  const handleConfirm = () => {
    if (selectedResponse) {
      onSelect(selectedResponse);
      setIsOpen(false);
      setSelectedResponse(null);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSelectedResponse(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {showTrigger && (
        <DialogTrigger asChild>
          <button className="p-2 rounded font-serif text-lg hover:bg-slate-200 px-3 transition-colors">
            T
          </button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl h-[600px] flex flex-col p-0 overflow-hidden">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>{t("chats.cannedResponsesTitle")}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Categories Sidebar */}
          <div className="w-1/3 border-r bg-slate-50 overflow-y-auto">
            <div className="p-2 flex flex-col gap-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategoryId(category.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-md text-sm transition-colors",
                    selectedCategoryId === category.id ||
                      (!selectedCategoryId && categories[0]?.id === category.id)
                      ? "bg-white border shadow-sm font-semibold text-primary"
                      : "hover:bg-slate-100 text-slate-600",
                  )}
                >
                  {category.name}
                </button>
              ))}
              {categories.length === 0 && !isLoading && (
                <div className="p-4 text-xs text-slate-400 text-center">
                  {t("chats.noCategoriesFound")}
                </div>
              )}
            </div>
          </div>

          {/* Responses List */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="p-4 border-b">
              <Input
                placeholder={t("chats.searchResponses")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-4 h-4 text-slate-400" />}
                className="h-9"
              />
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center h-full text-slate-400">
                  {t("chats.loading")}
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {filteredResponses.map((response) => (
                    <button
                      key={response.id}
                      onClick={() => handleSelect(response.text)}
                      className={cn(
                        "w-full text-left p-3 rounded-lg border transition-all group",
                        selectedResponse === response.text
                          ? "border-primary bg-primary/5 shadow-sm"
                          : "hover:border-slate-400 hover:bg-slate-50",
                      )}
                    >
                      <p
                        className={cn(
                          "text-sm line-clamp-3 group-hover:text-black",
                          selectedResponse === response.text
                            ? "text-primary font-medium"
                            : "text-slate-700",
                        )}
                      >
                        {response.text}
                      </p>
                    </button>
                  ))}
                  {filteredResponses.length === 0 && (
                    <div className="text-center py-8 text-slate-400">
                      {t("chats.noResponsesFound")}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter className="p-4 border-t flex justify-between items-center sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => handleOpenChange(false)}
            className="text-slate-500"
          >
            {t("chats.cancel")}
          </Button>
          <div className="flex gap-2">
            <Link to="/canned-responses">
              <Button variant="outline" className="flex gap-2">
                <PlusCircle className="w-4 h-4" />
                {t("chats.createCannedResponses")}
              </Button>
            </Link>
            <Button disabled={!selectedResponse} onClick={handleConfirm}>
              {t("chats.add")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
